/**
 * WordPress dependencies.
 */
import { render, Fragment, useState, useEffect } from '@wordpress/element';
import { Panel, PanelBody, Disabled, Notice, TabPanel, Button, PanelRow, Icon, DropdownMenu } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { applyFilters, addFilter } from '@wordpress/hooks';
import { useDispatch, useSelect } from '@wordpress/data';
import { getFragment } from '@wordpress/url';
import apiFetch from '@wordpress/api-fetch';
import { store as noticesStore } from '@wordpress/notices';
import { NoticeList, SnackbarList } from '@wordpress/components';

/**
 * Exteranl dependencies.
 */
import { replace, isEmpty, has, last, merge, filter } from 'lodash';

/**
 * Inner dependencies.
 */
import { getParsedVar } from '../utils';
import {
	ColorControl,
} from '../../extensions/controls/color-options';
import '../../extensions/store/settings';
import styling from '../inline-css';

const copySiteOptions = ( obj ) => {
	function handler( event ) {
		event.clipboardData.setData( 'text/plain', JSON.stringify( obj ) );
		event.preventDefault();
		document.removeEventListener( 'copy', handler, true );
	}

	document.addEventListener( 'copy', handler, true );
	document.execCommand( 'copy' );
};

const App = ( props ) => {
	const { styleId, gStyle, changeSiteOptions, appState } = props;

	const siteOptions = useSelect( ( select ) => {
		return select( 'gutenify/settings' ).getSiteOptions();
	} );

	let coreStyles = {};
	let gutenifyStyles = {};

	if ( has( siteOptions, 'coreSettings.styles' ) ) {
		coreStyles = siteOptions.coreSettings.styles;
	}

	if ( has( siteOptions, 'settings.styles' ) ) {
		gutenifyStyles = siteOptions.settings.styles;
	}

	const setCoreStyles = ( data ) => {
		changeSiteOptions( merge(
			siteOptions,
			{
				coreSettings: {
					styles: merge( gStyle.styles, data ),
				},
			},
		) );
	};

	const setGutenifyStyles = ( data ) => {
		changeSiteOptions( merge(
			siteOptions,
			{
				settings: {
					styles: merge( gutenifyStyles, data ),
				},
			},
		) );
	};

	// const [ gutenifyStyles, setGutenifyStyles ] = useState( {} );

	const urlFragment = getFragment( window.location.href );
	const activePanel = replace( urlFragment, '#', '' );

	const getColorCode = ( colorString ) => {
		let colorCode = colorString;
		if ( colorString.indexOf( 'var(' ) > -1 ) {
			const parsedVar = getParsedVar( colorString );
			if ( ! isEmpty( parsedVar ) ) {
				if ( parsedVar.includes( 'preset' ) && parsedVar.includes( 'color' ) && has( gStyle, 'settings.color.palette.theme' ) ) {
					const presetColor = gStyle.settings.color.palette.theme.filter( ( themeColor ) => themeColor.slug === last( parsedVar ) );
					if ( ! isEmpty( presetColor ) ) {
						colorCode = presetColor[ 0 ].color;
					}
				} else if ( parsedVar.includes( 'custom' ) && parsedVar.includes( 'color' ) && has( gStyle, `settings.custom.color.${ last( parsedVar ) }` ) ) {
					colorCode = gStyle.settings.custom.color[ last( parsedVar ) ];
				}
			}

			if ( colorCode.indexOf( 'var(' ) > -1 ) {
				colorCode = getColorCode( colorCode );
			}
		}

		return colorCode;
	};

	// useEffect( ( ) => {
	// 	apiFetch( {
	// 		path: `/gutenify/v1/get_site_options`,
	// 		method: 'get',
	// 	} )
	// 		.then( ( res ) => {
	// 			if ( res.success && res.response ) {
	// 				setGutenifyStyles( res.response.styles );
	// 			}
	// 		} );
	// }, [ ] );

	const textColor = has( coreStyles, 'color.text' ) ? getColorCode( coreStyles.color.text ) : '';
	const backgroundColor = has( coreStyles, 'color.background' ) ? getColorCode( coreStyles.color.background ) : '';
	const h1Color = has( coreStyles, 'elements.h1.color.text' ) ? getColorCode( coreStyles.elements.h1.color.text ) : '';
	const buttonBackgroundColor = has( coreStyles, 'blocks.core/button.color.background' ) ? getColorCode( coreStyles.blocks[ 'core/button' ].color.background ) : '';
	const buttonTextColor = has( coreStyles, 'blocks.core/button.color.text' ) ? getColorCode( coreStyles.blocks[ 'core/button' ].color.text ) : '';

	const linkColor = has( coreStyles, 'elements.link.color.text' ) ? getColorCode( coreStyles.elements.link.color.text ) : '';
	const linkHoverColor = has( gutenifyStyles, 'elements.linkHover.color.text' ) ? getColorCode( gutenifyStyles.elements.linkHover.color.text ) : '';

	const buttonHoverTextColor = has( gutenifyStyles, 'blocks.core/button.hover.color.text' ) ? getColorCode( gutenifyStyles.blocks[ 'core/button' ].hover.color.text ) : '';
	const buttonHoverBackgroundColor = has( gutenifyStyles, 'blocks.core/button.hover.color.background' ) ? getColorCode( gutenifyStyles.blocks[ 'core/button' ].hover.color.background ) : '';
	return <Disabled isDisabled={ appState.isSettingsDisabled }>
		<div className="gutenify-field-wrapper gutenify-field-grid">
			<div className="gutenify-field-label">
				<label htmlFor="text-color-control">{ __( 'Text Color' ) }</label>
			</div>
			<div className="gutenify-field-control">
				<ColorControl
					// label={ __( 'Text Color' ) }
					onChange={ ( color ) => {
						const data = merge( coreStyles, {
							color: {
								text: color,
							},
						} );
						setCoreStyles( { ...data } );
					} }
					value={ textColor }
					clearable={ false }
					id="ext-color-control"
				/>

			</div>
		</div>
		<div className="gutenify-field-wrapper gutenify-field-grid">
			<div className="gutenify-field-label">
				<label htmlFor="site-background-color-control">{ __( 'Site Background Color' ) }</label>
			</div>
			<div className="gutenify-field-control">
				<ColorControl
					// label={ __( 'Site Background Color' ) }
					onChange={ ( color ) => {
						const data = merge( coreStyles, {
							color: {
								background: color,
							},
						} );
						setCoreStyles( { ...data } );
					} }
					value={ backgroundColor }
					clearable={ false }
					id="site-background-color-control"
				/>
			</div>
		</div>
		<div className="gutenify-field-wrapper gutenify-field-grid">
			<div className="gutenify-field-label">
				<label htmlFor="site-background-color-control">{ __( 'Heading Color' ) }</label>
			</div>
			<div className="gutenify-field-control">
				<ColorControl
					// label={ __( 'Heading Color' ) }
					onChange={ ( color ) => {
						const data = merge( coreStyles, {
							elements: {
								h1: {
									color: {
										text: color,
									},
								},
								h2: {
									color: {
										text: color,
									},
								},
								h3: {
									color: {
										text: color,
									},
								},
								h4: {
									color: {
										text: color,
									},
								},
								h5: {
									color: {
										text: color,
									},
								},
								h6: {
									color: {
										text: color,
									},
								},
							},
						} );
						setCoreStyles( { ...data } );
					} }
					value={ h1Color }
					clearable={ false }
				/>
			</div>
		</div>

		<div className="gutenify-field-wrapper gutenify-field-grid">
			<div className="gutenify-field-label">
				<label htmlFor="site-background-color-control">{ __( 'Button Color' ) }</label>
			</div>
			<div className="gutenify-field-control">
				<TabPanel
					className="gutenify-editor-tab-panel gutenify-editor-tab-ovel"
					activeClass="active-tab"
					tabs={ [
						{
							name: 'button-color-normal',
							title: __( 'Normal' ),
							className: 'gutenify-color-normal-tab',
						},
						{
							name: 'button-color-hover',
							title: __( 'Hover' ),
							className: 'gutenify-color-hover-tab',
						},
					] }
				>
					{ ( tabInner ) => {
						return <>
							{ 'button-color-normal' === tabInner.name && <>
								<ColorControl
									label={ __( 'Button background Color' ) }
									onChange={ ( color ) => {
										const data = merge( coreStyles, {
											blocks: {
												'core/button': {
													color: {
														background: color,
													},
												},
											},
										} );
										setCoreStyles( { ...data } );
									} }
									value={ buttonBackgroundColor }
									clearable={ false }
								/>
								<ColorControl
									label={ __( 'Button Text Color' ) }
									onChange={ ( color ) => {
										const data = merge( coreStyles, {
											blocks: {
												'core/button': {
													color: {
														text: color,
													},
												},
											},
										} );
										setCoreStyles( { ...data } );
									} }
									value={ buttonTextColor }
									clearable={ false }
								/>

							</>
							}
							{ 'button-color-hover' === tabInner.name && <>
								<ColorControl
									label={ __( 'Button Hover Text Color' ) }
									onChange={ ( color ) => {
										const data = merge( gutenifyStyles, {
											blocks: {
												'core/button': {
													hover: {
														color: {
															text: color,
														},
													},
												},
											},
										} );
										setGutenifyStyles( { ...data } );
									} }
									value={ buttonHoverTextColor }
									clearable={ false }
								/>
								<ColorControl
									label={ __( 'Button Hover Background Color' ) }
									onChange={ ( color ) => {
										const data = merge( gutenifyStyles, {
											blocks: {
												'core/button': {
													hover: {
														color: {
															background: color,
														},
													},
												},
											},
										} );
										setGutenifyStyles( { ...data } );
									} }
									value={ buttonHoverBackgroundColor }
									clearable={ false }
								/>
							</> }
						</>;
					} }
				</TabPanel>

			</div>
		</div>

		<div className="gutenify-field-wrapper gutenify-field-grid">
			<div className="gutenify-field-label">
				<label htmlFor="site-background-color-control">{ __( 'Link Color' ) }</label>
			</div>
			<div className="gutenify-field-control">
				<TabPanel
					className="gutenify-editor-tab-panel gutenify-editor-tab-ovel"
					activeClass="active-tab"
					tabs={ [
						{
							name: 'link-color-normal',
							title: __( 'Normal' ),
							className: 'gutenify-color-normal-tab',
						},
						{
							name: 'link-color-hover',
							title: __( 'Hover' ),
							className: 'gutenify-color-hover-tab',
						},
					] }
				>
					{ ( tabInner ) => {
						return <>
							{ 'link-color-normal' === tabInner.name && <>
								<ColorControl
									label={ __( 'Link Color' ) }
									onChange={ ( color ) => {
										const data = merge( coreStyles, {
											elements: {
												link: {
													color: {
														text: color,
													},
												},
											},
										} );
										setCoreStyles( { ...data } );
									} }
									value={ linkColor }
									clearable={ false }
								/>
							</> }
							{ 'link-color-hover' === tabInner.name && <>
								<ColorControl
									label={ __( 'Link Hover Color' ) }
									onChange={ ( color ) => {
										const data = merge( gutenifyStyles, {
											elements: {
												linkHover: {
													color: {
														text: color,
													},
												},
											},
										} );
										setGutenifyStyles( { ...data } );
									} }
									value={ linkHoverColor }
									clearable={ false }
								/>
							</> }
						</>;
					} }
				</TabPanel>

			</div>
		</div>
	</Disabled>;
};

addFilter( 'gutenify-admin-settings-design-panel', 'gutenify/admin/settings/panel/general', ( content, props ) => (
	[ ...content, <Fragment key="gutenify-admin-settings-panel-general">
		<PanelBody title={ __( 'Colors' ) } initialOpen={ 'general' === props.activePanel }>
			{ /* <div>{ __( 'Post Type' ) }<p className="description">{ __( 'Choose post types to activate Gutenburg.' ) }</p></div> */ }
			<App { ...props } />
		</PanelBody>
	</Fragment> ]
) );

const DesignTab = ( props ) => {
	const { styleId, gStyle, changeSiteOptions, siteOptions } = props;
	useEffect( ( ) => {
		if ( styleId && gStyle ) {
			apiFetch( {
				path: `/wp/v2/global-styles/${ styleId }`,
				method: 'get',
			} )
				.then( ( res ) => {
					changeSiteOptions( merge(
						siteOptions,
						{
							coreSettings: {
								styles: merge( gStyle.styles, res.styles ),
							},
						},
					) );
				} );
		}
	}, [ styleId, gStyle ] );
	const { createSuccessNotice } = useDispatch( noticesStore );
	const { removeNotice } = useDispatch( noticesStore );
	const notices = useSelect(
		( select ) => select( noticesStore ).getNotices(),
		[]
	);
	const snackbarNotices = filter( notices, {
		type: 'snackbar',
	} );
	return <>
		<Fragment key="gutenify-admin-settings-panel">
			<Panel className="gutenify-setting-panel-toolbar-area">
				<PanelRow>
					<div></div>
					<div><DropdownMenu
						icon={ 'ellipsis' }
						label="Select a direction"
						controls={ [
							{
								title: 'Copy core Options',
								icon: 'admin-page',
								onClick: () => {
									copySiteOptions( props.siteOptions.coreSettings );
									createSuccessNotice( __( 'Core Settings copied.' ), {
										type: 'snackbar',
									} );
								},
							},
							{
								title: 'Copy Gutenify Options',
								icon: 'admin-page',
								onClick: () => {
									copySiteOptions( props.siteOptions.settings );
									createSuccessNotice( __( 'Gutenify Settings copied.' ), {
										type: 'snackbar',
									} );
								},
							},
						] }
					/></div>
				</PanelRow>
			</Panel>
			<Panel header={ __( '' ) }>
				{ applyFilters( 'gutenify-admin-settings-design-panel', [], props ) }
			</Panel>
			<Panel className="gutenify-setting-panel-toolbar-area">
				<PanelRow>
					<Button variant="secondary" onClick={ () => {
						if ( ! window.confirm( 'Are you sure to reset design settings?' ) ) {
							return false;
						}
						apiFetch( {
							path: `/wp/v2/global-styles/${ styleId }`,
							method: 'POST',
							data: {
								styles: {},
							},
						} )
							.then( ( ) => {
								changeSiteOptions( merge(
									siteOptions,
									{
										coreSettings: {
											styles: {},
										},
									},
								) );
							} )
							.catch( ( err ) => {
								console.log( err );
							} );

						apiFetch( {
							path: `/gutenify/v1/update_site_options`,
							method: 'POST',
							data: {
								styles: {},
							},
						} )
							.then( ( ) => {
								changeSiteOptions( merge(
									siteOptions,
									{
										settings: {
											styles: {},
										},
									},
								) );
							} )
							.catch( ( err ) => {
								console.log( err );
							} );

						apiFetch( {
							path: `/gutenify/v1/update_global_styles`,
							method: 'POST',
							data: {
								css: '',
								adminCss: '',
							},
						} )
							.then( ( res ) => {
								console.log( res );
							} )
							.catch( ( err ) => {
								console.log( err );
							} );

						location.reload();
					} }>Reset</Button>
					<div>
						<Button variant="primary" onClick={ () => {
							apiFetch( {
								path: `/wp/v2/global-styles/${ styleId }`,
								method: 'POST',
								data: siteOptions.coreSettings,
							} )
								.then( ( res ) => {
									// console.log( res );
								} )
								.catch( ( err ) => {
									// console.log( err );
								} );

							apiFetch( {
								path: `/gutenify/v1/update_site_options`,
								method: 'POST',
								data: siteOptions.settings,
							} )
								.then( ( res ) => {
									// console.log( res );
								} )
								.catch( ( err ) => {
									// console.log( err );
								} );

							const coreStyles = siteOptions.coreSettings.styles;
							const gutenifyStyles = siteOptions.settings.styles;

							const css = styling( coreStyles, gutenifyStyles );
							const adminCss = styling( coreStyles, gutenifyStyles, true );
							apiFetch( {
								path: `/gutenify/v1/update_global_styles`,
								method: 'POST',
								data: {
									css,
									adminCss,
								},
							} )
								.then( ( res ) => {

								} )
								.catch( ( err ) => {
									console.log( err );
								} );
							createSuccessNotice( __( 'Settings saved.' ), {
								type: 'snackbar',
							} );
						} }>Save</Button>

					</div>
				</PanelRow>
			</Panel>
		</Fragment>
		<SnackbarList
			notices={ snackbarNotices }
			className="edit-navigation-notices__snackbar-list"
			onRemove={ removeNotice }
		/>
	</>;
};

export default DesignTab;

